import React, { useRef, useState } from 'react';

import * as Yup from 'yup';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';

import Info from '@material-ui/icons/Info';
import GetAppRounded from '@material-ui/icons/GetAppRounded';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import BOMPaymentList from './BOMPaymentList';
import AddBankDetails from './AddBankDetails';

import Button from 'components/CustomButtons/Button';
import PaymentProcessing from '../common/PaymentProcessing';
import { usePaymentMethod } from 'components/PaymentMethod/context';

import { setDataLayer } from 'commons/gtm/dataLayerWrapper';
import { getLanguage } from 'utils/language/language';
import { upIdSelector, userData } from 'redux/modules/session/selectors';
import { getPaymentMethodLabels } from 'redux/modules/dynamicLabels/selectors';

import {
	BILLS_LIST,
	CURRENCY,
	EVENTS_FAILURE,
	EVENTS_SUCCESS,
	PAYMENT_METHOD,
	PAYMENT_METHODS,
} from 'views/Bills/constants';
import { MY_BILLS_PAY } from 'components/PaymentMethod/constant';

import PaymentService from 'services/payments/payment';
import { BOMPaymentToken, BankDetails, PayWith, PaymentMethodProps, PaymentStatusType } from '../../type';

import styles, { AntSwitch as Switch } from 'assets/jss/material-dashboard-pro-react/components/paymentMethodsStyle';
import { makeStyles } from '@material-ui/core/styles';

import config from 'utils/api/config';
import { loadQualtrics } from 'utils/qualtrics';
import { BankMuscatIntroAr, BankMuscatIntroEn, env } from 'config/app.config';
import { remoteConfigSelector } from 'redux/modules/settings/remoteConfig/selectors';
import { isBOMIntroOn } from 'utils/killswitch';

// @ts-ignore
const useStyles = makeStyles(styles);

const BOMPayment = ({ bills, onPaymentResult, paymentPreference, referrer = '' }: PaymentMethodProps) => {
	const classes = useStyles();
	const {
		tokens: { BOM },
		onAddBankAlias,
	} = usePaymentMethod();

	const isEn = getLanguage() === 'en';
	const accId = useSelector(upIdSelector);
	const userDetails = useSelector(userData);
	const paymentMethodLabels = useSelector(getPaymentMethodLabels);

	// kill switch to check if Bank Muscat are to be shown
	const remoteConfig = useSelector(remoteConfigSelector);
	const isBankMuscatIntroEnabled = isBOMIntroOn(remoteConfig);

	const [addCardDialogOpen, setAddCardDialogOpen] = useState(false);

	const onAddingBankAlias = (bankAlias: BOMPaymentToken): void => {
		onAddBankAlias(bankAlias);
		setAddCardDialogOpen(false);
	};

	const onSubmit = (values: BankDetails, actions: FormikHelpers<BankDetails>) => {
		setDataLayer(MY_BILLS_PAY, getLanguage(), PAYMENT_METHOD, accId, null, {
			stepNo: 2,
			paymentMethod: PAYMENT_METHODS.BANK_MUSCAT,
		});

		const data: types.payment.BankPayment = {
			name: userDetails.userName,
			email: userDetails.email,
			mobileNumber: userDetails?.contactNumber,
			items: bills as types.payment.paymentItem[],
			createDefaultPayment: values.defaultOption,
			saveBankMuscatToken: BOM.defaultToken?.savedOption ?? values.savedOption,
			totalPrice: bills.reduce((acc, curr) => {
				return acc + parseFloat(curr?.unitPrice?.toString());
			}, 0),
			...(BOM.defaultToken?.isUnSaved
				? {
						bankMuscatToken: {
							mobileNumber: BOM.defaultToken?.mobileNumber,
							paymentAliasId: BOM.defaultToken?.paymentAliasId,
						},
				  }
				: { useToken: BOM.defaultToken?.id }),
		};

		PaymentService.payWithBOM(data).subscribe(
			({ response }) => {
				actions.setSubmitting(false);

				// Declare variable to hold payment status
				let _paymentStatus: PaymentStatusType;

				// Check if payment reference number exists
				if (response?.data?.transactionReferenceNumber) {
					// Set payment status to INITIATED if payment reference number is present
					_paymentStatus = PaymentStatusType.INITIATED;
				} else if (response?.data?.ticketNumber) {
					// If payment reference number doesn't exist but ticket number does, set status to FAILED
					_paymentStatus = PaymentStatusType.FAILED;
				} else {
					// If neither payment reference number nor ticket number is present, set status to ERROR
					_paymentStatus = PaymentStatusType.ERROR;
				}

				if (onPaymentResult) onPaymentResult(_paymentStatus, response?.data);
				setTimeout(() => {
					loadQualtrics({
						activity_type: `Bill Payment with Bank Muscat  - ${_paymentStatus}`,
					});
				}, 2000);

				setDataLayer(EVENTS_SUCCESS[referrer], getLanguage(), null, accId, null, {
					...(referrer === BILLS_LIST && { stepNo: 2 }),
					paymentMethod: PAYMENT_METHODS.BANK_MUSCAT,
					referenceID: response?.data?.transactionReferenceNumber,
					value: data.totalPrice,
					currency: CURRENCY.OMR,
					items: data.items.map((paymentData) => {
						return {
							...(paymentData.month && { billPeriod: paymentData.month }),
							billAmount: paymentData.unitPrice,
						};
					}),
				});
			},
			(error: any) => {
				actions.setSubmitting(false);
				if (onPaymentResult) onPaymentResult(PaymentStatusType.ERROR, error);
				setTimeout(() => {
					loadQualtrics({
						activity_type: `Bill Payment with Bank Muscat  - ${PaymentStatusType.ERROR}`,
					});
				}, 2000);
				setDataLayer(EVENTS_FAILURE[referrer], getLanguage(), null, accId, null, {
					...(referrer === BILLS_LIST && { stepNo: 2 }),
					paymentMethod: PAYMENT_METHODS.BANK_MUSCAT,
					errorMsg: '', // TODO(BM): add error message from api response
					errSrc: '', // TODO(BM): add error source bankMuscat or Omantel from API response
					errTktID: '', //TODO(BM): add ticket id from api response
				});
			}
		);
	};

	const FormSchema = Yup.object().shape({
		savedOption: Yup.boolean(),
		defaultOption: Yup.boolean(),
	});

	const source = isEn ? BankMuscatIntroEn : BankMuscatIntroAr;
	const introLink = config.protocol[env] + config.sources.wcm[env] + source;

	return (
		<>
			<Formik
				validateOnBlur
				validateOnChange
				onSubmit={onSubmit}
				validationSchema={FormSchema}
				initialValues={{
					savedOption: false,
					agreeTermCondition: false,
					defaultOption: paymentPreference === PayWith.BM,
				}}
			>
				{(props: FormikProps<BankDetails>) => {
					return (
						<>
							{props.isSubmitting && <PaymentProcessing visible />}
							<Form
								className={classes.paymentMethodWrapper}
								onSubmit={props.handleSubmit}
								autoComplete="off"
							>
								<div className={classes.billDetailsContainer}>
									<p className={classes.infoNote}>{paymentMethodLabels?.bankMuscatNote}</p>
								</div>
								<BOMPaymentList />
								<div className={classes.billDetailsContainer}>
									<Button
										fullWidth
										color="secondary-light"
										onClick={() => setAddCardDialogOpen(true)}
										className={classNames(classes.addCardButton)}
									>
										{paymentMethodLabels?.add_payment_alias}
									</Button>
								</div>
								<div className={classes.billDetailsContainer}>
									<div className={classNames(classes.accordionElement)}>
										<FormControlLabel
											className={classes.switchLabel}
											control={
												<Switch
													id="default_bank_method"
													name="defaultOption"
													checked={props.values.defaultOption}
													onChange={props.handleChange}
												/>
											}
											label={paymentMethodLabels?.defaultPaymentMethod}
										/>
									</div>
								</div>
								<div className={classes.billDetailsContainer}>
									<p className={classNames(classes.infoNote, classes.paymentNote)}>
										<Info />
										{paymentMethodLabels?.bom_payment_approve_note}
									</p>
								</div>
								{isBankMuscatIntroEnabled && (
									<div className={classes.billDetailsContainer}>
										<p className={classNames(classes.infoNote, classes.paymentNote)}>
											<GetAppRounded />
											{paymentMethodLabels?.bomIntro?.quick_note}
											<a
												href={introLink}
												target="_blank"
												rel="noopener noreferrer"
												className={classes.link}
											>
												{paymentMethodLabels?.bomIntro?.click_here}
											</a>
										</p>
									</div>
								)}
								<div className={classes.billDetailsContainer}>
									<Button
										fullWidth
										type="submit"
										color="primary-light"
										disabled={props.isSubmitting || !BOM.defaultToken}
										className={classes.makePaymentButton}
									>
										{paymentMethodLabels?.confirmPay}
									</Button>
								</div>
							</Form>
						</>
					);
				}}
			</Formik>
			<AddBankDetails
				onAddCard={onAddingBankAlias}
				isOpen={addCardDialogOpen}
				onClose={() => setAddCardDialogOpen((state) => !state)}
			/>
		</>
	);
};

export default BOMPayment;
